import BaseAPIService from "./BaseAPIService";
import store from "@/store";
import gql from "graphql-tag";
import { getProvider } from "../vue-apollo";

class UserService extends BaseAPIService {
  constructor() {
    super();
  }

  getById(id) {
    return new Promise((resolve, reject) => {
      const query = gql`
        query user($id: ID!) {
          user(id: $id) {
            id
            username
            email
            firstName
            lastName
            telephone
            address
            city
            country
            business {
              name
              registrationNumber
              address
              postalCode
              city
              country
              industry
            }
          }
        }
      `;
      const variables = {
        id,
      };
      this.query({ query, variables })
        .then((result) => {
          resolve(result.data.user);
        })
        .catch(reject);
    });
  }

  getPersonalInfo() {
    const userId = getUserId();
    return this.getById(userId);
  }

  signin(username, password) {
    return new Promise((resolve, reject) => {
      const query = gql`
        query signin($username: String!, $password: String!) {
          signin(username: $username, password: $password) {
            token
            user {
              id
              username
              email
              firstName
              lastName
              fullName
            }
            expiresDate
          }
        }
      `;
      const variables = {
        username,
        password,
      };
      this.query({ query, variables })
        .then((result) => {
          const signInResult = result.data.signin;
          setAuth(signInResult);
          resolve(signInResult);
        })
        .catch(reject);
    });
  }

  signup(user, business, activateUrl) {
    return new Promise((resolve, reject) => {
      const mutation = gql`
        mutation signup(
          $activateUrl: String!
          $user: SignUpInput!
          $business: BusinessInput
        ) {
          signupBusinessUser(
            activateUrl: $activateUrl
            user: $user
            business: $business
          ) {
            token
            user {
              id
              username
              email
              firstName
              lastName
              fullName
            }
            expiresDate
          }
        }
      `;
      const variables = { activateUrl, user, business };
      this.mutate({ mutation, variables })
        .then((result) => {
          setAuth(result.data.signupBusinessUser);
          resolve();
        })
        .catch(reject);
    });
  }

  activate(secretCode) {
    return new Promise((resolve, reject) => {
      const query = gql`
            query {
                activate (secretCode: "${secretCode}")
              }
            `;
      this.query({ query })
        .then((result) => {
          if (result.activate === true) {
            resolve();
          } else {
            reject(new Error("Invalid activation code."));
          }
        })
        .catch(reject);
    });
  }

  async updatePersonalInfo(data) {
    const mutation = gql`
      mutation updateUserPersonalInfo(
        $firstName: String
        $lastName: String
        $city: String
        $country: String
        $telephone: String
        $address: String
        $postalCode: String
      ) {
        updateUserPersonalInfo(
          firstName: $firstName
          lastName: $lastName
          city: $city
          country: $country
          telephone: $telephone
          address: $address
          postalCode: $postalCode
        ) {
          id
          username
          email
          firstName
          lastName
          telephone
          city
          country
          address
          fullName
        }
      }
    `;
    const {
      firstName,
      lastName,
      email,
      telephone,
      city,
      country,
      address,
    } = data;
    const variables = {
      firstName,
      lastName,
      email,
      telephone,
      city,
      country,
      address,
    };
    const result = await this.mutate({ mutation, variables });
    const auth = getAuth();
    auth.user = result.data.updateUserPersonalInfo;
    setAuth(auth);
    return auth.user;
  }

  async updateBusinessInfo(data) {
    const mutation = require("../graphql/mutations/updateUserBusinessInfo.gql");
    const result = await this.mutate({ mutation, variables: data });
    return result.data.updateUserBusinessInfo;
  }

  changePassword(password) {
    return new Promise((resolve, reject) => {
      const id = getUserId();
      const mutation = gql`
        mutation changePassword($id: ID!, $password: String!) {
          changePassword(id: $id, password: $password)
        }
      `;
      const variables = {
        id,
        password,
      };
      this.mutate({ mutation, variables })
        .then(() => {
          resolve();
        })
        .catch(reject);
    });
  }

  /**
   * Check availability of specific email.
   * @param {String} email Email that we want to check.
   */
  isExistsBusinessUserEmail(email) {
    return new Promise((resolve, reject) => {
      const query = gql`
        query isExistsBusinessUserEmail($email: String!) {
          isExistsBusinessUserEmail(email: $email)
        }
      `;
      const variables = {
        email,
      };
      this.query({ query, variables })
        .then((result) => {
          resolve(result.data.isExistsBusinessUserEmail);
        })
        .catch(reject);
    });
  }

  requestResetPassword(email, resetUrl) {
    return new Promise((resolve, reject) => {
      const mutation = gql`
            mutation {
                requestResetPassword(email: "${email}", resetUrl: "${resetUrl}")
              }
            `;
      this.mutate({ mutation })
        .then(() => {
          resolve();
        })
        .catch(reject);
    });
  }

  verifyResetPasswordCode(code) {
    return new Promise((resolve, reject) => {
      const query = gql`
            query {
                verifyResetPasswordCode(code: "${code}")
              }
            `;
      this.query({ query })
        .then((result) => {
          resolve(result.data.verifyResetPasswordCode);
        })
        .catch(reject);
    });
  }

  resetPassword(password, code) {
    return new Promise((resolve, reject) => {
      const mutation = gql`
            mutation {
                resetPassword(password: "${password}", code: "${code}")
              }
            `;
      this.mutate({ mutation })
        .then(() => {
          resolve();
        })
        .catch(reject);
    });
  }

  testPassword(password) {
    return /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/.test(password);
  }

  async getOwnedBusiness() {
    const query = require("../graphql/queries/ownedBusiness.gql");
    const result = await this.query({
      query,
    });
    return result.data.ownedBusiness;
  }

  // addBusinessUser(businessId: ID!, userId: ID!, role: String!): Boolean
}

export default UserService;

function setAuth(signInResult) {
  const auth = JSON.stringify(signInResult);
  localStorage.setItem("auth", auth);
  localStorage.setItem("token", signInResult.token);
  store.commit("setAuth", signInResult);
}

function removeAuth() {
  localStorage.removeItem("auth");
  localStorage.removeItem("token");
  store.commit("removeAuth");
}

export const isLoggedIn = () => {
  const auth = getAuth();
  return auth && auth.user;
};

export async function isValidLoginToken() {
  const token = getToken();
  if (!token) return false;

  const options = {
    query: gql`
      query {
        isValidLoginToken
      }
    `,
    fetchPolicy: "network-only",
  };

  const provider = getProvider();

  try {
    const result = await provider.defaultClient.query(options);
    return result.data.isValidLoginToken;
  } catch {
    return false;
  }
}

export const getAuth = () => {
  const authData = localStorage.getItem("auth");
  if (authData) {
    return JSON.parse(authData);
  } else {
    return null;
  }
};

export const getToken = () => {
  return localStorage.getItem("token");
};

export const initAuth = () => {
  const auth = getAuth();
  if (auth) {
    store.commit("setAuth", auth);
  }
};

export const getUserId = () => {
  return isLoggedIn() ? store.state.auth.user.id : null;
};

export const logout = () => {
  removeAuth();
};

export const isEmailValid = (email) => {
  const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
  return regex.test(email);
};
